import React, { useEffect } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";

// Context
import { ThemeProvider } from "./context/ThemeContext";
import { SiteSettingsProvider } from "./context/SiteSettingsContext";

// Layout Components
import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";

// Pages
import HomePage from "./pages/HomePage";
import AboutPage from "./pages/AboutPage";
import PackagesPage from "./pages/PackagesPage";
import PackageDetailPage from "./pages/PackageDetailPage";
import DestinationsPage from "./pages/DestinationsPage";
import DayToursPage from "./pages/DayToursPage";
import FleetPage from "./pages/FleetPage";
import ContactPage from "./pages/ContactPage";
import AIPlannerPage from "./pages/AIPlannerPage";
import AdminPage from "./pages/AdminPage";
import LegalPage from "./pages/LegalPage";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Track visitor
const VisitorTracker = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    const trackVisitor = async () => {
      try {
        const visitorData = {
          page: pathname,
          user_agent: navigator.userAgent,
          referrer: document.referrer || 'direct',
          screen_size: `${window.innerWidth}x${window.innerHeight}`,
          timestamp: new Date().toISOString()
        };
        
        // Send to backend
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/visitors/track`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(visitorData)
        });
      } catch (err) {
        // Silently fail - don't disrupt user experience
        console.debug('Visitor tracking skipped');
      }
    };
    
    // Track after a small delay to avoid blocking page load
    const timer = setTimeout(trackVisitor, 1000);
    return () => clearTimeout(timer);
  }, [pathname]);
  
  return null;
};

function App() {
  return (
    <ThemeProvider>
      <SiteSettingsProvider>
      <div className="App bg-s-bg text-s-text transition-colors duration-300">
        <BrowserRouter>
          <ScrollToTop />
          <VisitorTracker />
          <AppContent />
        </BrowserRouter>
      </div>
      </SiteSettingsProvider>
    </ThemeProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const isAdmin = location.pathname === '/admin';
  return (
    <>
      {!isAdmin && <Navbar />}
      <main className={isAdmin ? 'min-h-screen' : 'min-h-screen pt-16 md:pt-0'}>
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/packages" element={<PackagesPage />} />
          <Route path="/packages/:id" element={<PackageDetailPage />} />
          <Route path="/destinations" element={<DestinationsPage />} />
          <Route path="/day-tours" element={<DayToursPage />} />
          <Route path="/fleet" element={<FleetPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/ai-planner" element={<AIPlannerPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/privacy-policy" element={<LegalPage type="privacy" />} />
          <Route path="/terms-conditions" element={<LegalPage type="terms" />} />
          <Route path="/refund-policy" element={<LegalPage type="refund" />} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <WhatsAppButton />}
    </>
  );
}

export default App;
